/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import {
  Card,
  useMediaQuery,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Modal,
  Stack,
} from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import { format } from 'date-fns';
import { useConfirm } from 'material-ui-confirm';
import { Link } from 'react-router-dom';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { TripMatch } from '@marageti/z4-sdk/lib/travel';
import { ReminderMap, getDueStatus } from '../../utils/reminders';

const ReminderCard = ({
  tripMatch,
  dismissReminder,
  snoozeReminder,
  showAssignedToName,
}: {
  tripMatch: TripMatch,
  dismissReminder: (tripMatchId: string) => void
  snoozeReminder: (tripMatchId: string, selectedDueAt: Date) => void,
  showAssignedToName: boolean,
}) => {
  const confirm = useConfirm();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const { isDue, isOverdue } = getDueStatus(tripMatch.reminder);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCalendar, setOpenCalendar] = useState(false);

  const traveler = tripMatch?.travelers[0];
  const travelerName = traveler && traveler.fullName;
  const dueDate = tripMatch.reminder.dueAt ? format(new Date(tripMatch.reminder.dueAt), 'dd MMM') : '';
  const ReminderIcon = ReminderMap[tripMatch.salesStage]?.[tripMatch.reminder.index]?.icon;
  const ReminderTitle = `${ReminderMap[tripMatch.salesStage]?.[tripMatch.reminder.index]?.title}:${travelerName && ` ${travelerName}'s Trip`}`;
  const ReminderDescription = ReminderMap[tripMatch.salesStage]?.[tripMatch.reminder.index]?.description || '';

  const handleCloseCalendar = () => {
    setOpenCalendar(false);
    setAnchorEl(null);
  };

  const onDismissClick = () => {
    confirm({
      title: 'Dismiss Follow Up Reminder?',
      description: "Are you sure you want to dismiss this reminder? It will be removed from your alerts and won't appear again until the next scheduled follow-up.",
      confirmationButtonProps: {
        color: 'primary',
      },
    })
      .then(() => {
        dismissReminder(tripMatch.id);
        setAnchorEl(null);
      })
      .catch(() => { });
  };

  const onSnoozeClick = (date: Date) => {
    snoozeReminder(tripMatch.id, date);
    handleCloseCalendar();
  };

  const today = new Date();
  const todayPlus24Hours = new Date(); todayPlus24Hours.setHours(todayPlus24Hours.getHours() + 24);
  const todayPlus1Year = new Date(); todayPlus1Year.setFullYear(todayPlus1Year.getFullYear() + 1);

  return (
    <>
      {isMobile ? (
        <Card
          sx={{ backgroundColor: isDue ? 'background.paper' : isOverdue ? 'warning.light' : 'background.paper' }}
        >
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" flexDirection="row" justifyContent="space-between">
                <Avatar
                  component={Link}
                  to={`/trip-matches/${tripMatch.id}`}
                  sx={{ backgroundColor: isDue ? 'common.grey' : isOverdue ? 'common.warning50' : 'background.paper' }}
                >
                  {ReminderIcon && <ReminderIcon color="primary" />}
                </Avatar>

                <Box
                  display="flex"
                  flexDirection="row"
                  gap={0.5}
                  alignItems="center"
                >
                  <Tooltip title="Send Message" placement="top">
                    <IconButton component={Link} to={`/trip-matches/${tripMatch.id}/messages/${tripMatch.travelerToPartnerId}`}>
                      <OutlinedIcons.OutlinedChatIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    onClick={(event) => {
                      setAnchorEl(event.currentTarget);
                    }}
                  >
                    <OutlinedIcons.OutlinedDotsHorizontalIcon color="primary" />
                  </IconButton>
                </Box>
              </Box>

              <Box
                flex="1"
                component={Link}
                to={`/trip-matches/${tripMatch.id}`}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Typography variant="h4">
                  {ReminderTitle}
                </Typography>
                <Typography variant="body" sx={{ mb: 1, fontSize: '14px' }}>{ReminderDescription}</Typography>
                <Stack direction="row" spacing={1}>
                  {isOverdue && (
                  <Chip
                    label={`Overdue: ${dueDate}`}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'background.paper',
                    }}
                  />
                  )}
                  {showAssignedToName && (
                  <Chip
                    label={tripMatch.assignedToName}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'background.paper',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  />
                  )}
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card
          sx={{
            backgroundColor: isDue ? 'background.paper' : isOverdue ? 'warning.light' : 'background.paper',
          }}
        >
          <CardContent>
            <Box display="flex" flexDirection="row" gap={2} alignItems="center" justifyContent="space-between">
              <Avatar
                component={Link}
                to={`/trip-matches/${tripMatch.id}`}
                sx={{ backgroundColor: isDue ? 'common.grey' : isOverdue ? 'common.warning50' : 'background.paper' }}
              >
                {ReminderIcon && <ReminderIcon color="primary" />}
              </Avatar>

              <Box
                flex="1"
                component={Link}
                to={`/trip-matches/${tripMatch.id}`}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Typography variant="h4">
                  {ReminderTitle}
                </Typography>
                <Typography variant="body" sx={{ mb: 1, fontSize: '14px' }}>{ReminderDescription}</Typography>
                <Stack direction="row" spacing={1}>
                  {isOverdue && (
                  <Chip
                    label={`Overdue: ${dueDate}`}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'background.paper',
                    }}
                  />
                  )}
                  {showAssignedToName && (
                  <Chip
                    label={tripMatch.assignedToName}
                    variant="outlined"
                    sx={{
                      maxWidth: '400px',
                      backgroundColor: 'background.paper',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  />
                  )}
                </Stack>
              </Box>

              <Box display="flex" flexDirection="row" gap={0.5} alignItems="center">
                <Tooltip title="Send Message" placement="top">
                  <IconButton component={Link} to={`/trip-matches/${tripMatch.id}/messages/${tripMatch.travelerToPartnerId}`}>
                    <OutlinedIcons.OutlinedChatIcon color="primary" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                  }}
                >
                  <OutlinedIcons.OutlinedDotsHorizontalIcon color="primary" />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          setOpenCalendar(true);
        }}
        >
          Snooze Until...
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDismissClick();
          }}
        >
          Dismiss
        </MenuItem>
      </Menu>
      <Modal
        open={openCalendar}
        onClose={handleCloseCalendar}
      >
        <Card sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          p: 1,
        }}
        >
          <DateCalendar
            value={today}
            minDate={todayPlus24Hours}
            maxDate={todayPlus1Year}
            onChange={onSnoozeClick}
          />
        </Card>
      </Modal>
    </>
  );
};

export default ReminderCard;
